/**
 * Meyar — Integrations grid (text-only).
 *
 * A deliberately typographic grid: twelve equal-height tiles, product name
 * only. No logos, no icons, no images — avoids all brand-asset questions
 * and keeps the grid visually consistent.
 *
 * Section-tone-aware:
 *   tone="light" — for use on a white / cream / tinted band
 *   tone="dark"  — for use on the deep-green Meyar dark band
 *
 * Behaviour:
 *   - Names that don't fit one line wrap gracefully; type size is uniform.
 *   - Hover picks up the Meyar accent border and a tiny lift.
 *   - Grid is 4 across ≥ lg, 3 across md, 2 across mobile.
 *
 * Trademark footnote is rendered by the parent section (Section 5) so this
 * component stays purely presentational.
 */

const INTEGRATIONS = [
  "Amazon Web Services",
  "Microsoft Azure",
  "Google Cloud",
  "GitHub",
  "GitLab",
  "Jira",
  "Slack",
  "Okta",
  "Docker",
  "Kubernetes",
  "Atlassian",
  "Cloudflare",
];

export function MeyarIntegrations({ tone = "light" }) {
  const dark = tone === "dark";

  const tile = dark
    ? "border-white/15 bg-white/[0.03] text-white/85 hover:bg-white/[0.06]"
    : "border-wafa-deep/10 bg-white text-wafa-deep hover:bg-wafa-cream/40";

  return (
    <ul
      role="list"
      data-testid="meyar-integrations"
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
    >
      {INTEGRATIONS.map((name) => (
        <li key={name}>
          <div
            data-testid={`integration-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`group flex h-24 items-center justify-center rounded-lg border px-5 py-6 text-center transition-all duration-200 hover:-translate-y-0.5 sm:h-28 ${tile}`}
            style={{
              // Hover-only accent border → Wafa green (#58AF58).
              transitionProperty: "transform, background-color, border-color",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#58AF58";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "";
            }}
          >
            <span className="font-heading text-base font-medium leading-snug sm:text-lg">
              {name}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

export const TRADEMARK_NOTICE =
  "All product names, logos and brands are the property of their respective owners. Their use indicates compatibility only and does not imply endorsement or affiliation.";
